import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CampaignRecipient {
  id: string;
  ownerId: string;
  ownerName: string | null;
  phoneNumber: string | null;
  status: "pending" | "sent" | "delivered" | "read" | "failed" | "skipped";
  skipReason: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  readAt: string | null;
  failedAt: string | null;
  errorMessage: string | null;
}

export interface CampaignAttribution {
  id: string;
  ownerId: string;
  appointmentId: string | null;
  saleId: string | null;
  attributedRevenue: number;
  daysToConversion: number | null;
}

export interface CampaignDetailMetrics {
  totalSent: number;
  totalDelivered: number;
  totalRead: number;
  totalFailed: number;
  totalSkipped: number;
  deliveryRate: number;
  readRate: number;
  totalAttributedRevenue: number;
  conversions: number;
  avgDaysToConversion: number | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapRecipient(row: any, ownerNameMap: Record<string, string>): CampaignRecipient {
  return {
    id: row.id,
    ownerId: row.owner_id,
    ownerName: ownerNameMap[row.owner_id] ?? null,
    phoneNumber: row.phone_number ?? null,
    status: row.status,
    skipReason: row.skip_reason ?? null,
    sentAt: row.sent_at ?? null,
    deliveredAt: row.delivered_at ?? null,
    readAt: row.read_at ?? null,
    failedAt: row.failed_at ?? null,
    errorMessage: row.error_message ?? null,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapAttribution(row: any): CampaignAttribution {
  return {
    id: row.id,
    ownerId: row.owner_id,
    appointmentId: row.appointment_id ?? null,
    saleId: row.sale_id ?? null,
    attributedRevenue: Number(row.attributed_revenue ?? 0),
    daysToConversion: row.days_to_conversion ?? null,
  };
}

function computeMetrics(
  recipients: CampaignRecipient[],
  attributions: CampaignAttribution[]
): CampaignDetailMetrics {
  const sent = recipients.filter((r) => r.status !== "skipped" && r.status !== "pending").length;
  const delivered = recipients.filter((r) => r.status === "delivered" || r.status === "read").length;
  const read = recipients.filter((r) => r.status === "read").length;
  const failed = recipients.filter((r) => r.status === "failed").length;
  const skipped = recipients.filter((r) => r.status === "skipped").length;

  const totalAttributedRevenue = attributions.reduce((s, a) => s + a.attributedRevenue, 0);
  const conversions = attributions.length;
  const conversionDays = attributions.filter((a) => a.daysToConversion !== null);
  const avgDaysToConversion =
    conversionDays.length > 0
      ? conversionDays.reduce((s, a) => s + (a.daysToConversion ?? 0), 0) / conversionDays.length
      : null;

  return {
    totalSent: sent,
    totalDelivered: delivered,
    totalRead: read,
    totalFailed: failed,
    totalSkipped: skipped,
    deliveryRate: sent > 0 ? delivered / sent : 0,
    readRate: delivered > 0 ? read / delivered : 0,
    totalAttributedRevenue,
    conversions,
    avgDaysToConversion,
  };
}

async function backfillCampaignAttribution(campaignId: string) {
  const { data: campaign } = await supabase
    .from("campaigns" as never)
    .select("id, clinic_id, dispatched_at")
    .eq("id", campaignId)
    .maybeSingle();

  const campaignRow = campaign as { clinic_id?: string; dispatched_at?: string | null } | null;
  if (!campaignRow?.clinic_id || !campaignRow.dispatched_at) return;

  const dispatchedAt = new Date(campaignRow.dispatched_at);
  if (Number.isNaN(dispatchedAt.getTime())) return;
  const attributionWindowEnd = new Date(dispatchedAt);
  attributionWindowEnd.setDate(attributionWindowEnd.getDate() + 30);

  const { data: recipients } = await supabase
    .from("campaign_recipients" as never)
    .select("owner_id")
    .eq("campaign_id", campaignId)
    .eq("clinic_id", campaignRow.clinic_id)
    .not("owner_id", "is", null);

  const ownerIds = Array.from(new Set(((recipients ?? []) as { owner_id: string | null }[])
    .map((row) => row.owner_id)
    .filter(Boolean) as string[]));
  if (ownerIds.length === 0) return;

  const { data: sales } = await supabase
    .from("sales")
    .select("id, owner_id, appointment_id, total, created_at")
    .eq("clinic_id", campaignRow.clinic_id)
    .eq("status", "PAGA")
    .in("owner_id", ownerIds)
    .gte("created_at", campaignRow.dispatched_at)
    .lte("created_at", attributionWindowEnd.toISOString());

  const saleRows = (sales ?? []).filter((sale) => sale.owner_id && sale.created_at);
  if (saleRows.length === 0) return;

  const { data: existingAttributions } = await supabase
    .from("campaign_attribution" as never)
    .select("sale_id")
    .eq("campaign_id", campaignId)
    .eq("clinic_id", campaignRow.clinic_id);

  const attributedSaleIds = new Set(((existingAttributions ?? []) as { sale_id: string | null }[])
    .map((row) => row.sale_id)
    .filter(Boolean));

  const rowsToInsert = saleRows
    .filter((sale) => !attributedSaleIds.has(sale.id))
    .map((sale) => ({
      campaign_id: campaignId,
      clinic_id: campaignRow.clinic_id,
      owner_id: sale.owner_id,
      appointment_id: sale.appointment_id ?? null,
      sale_id: sale.id,
      attributed_revenue: Number(sale.total ?? 0),
      days_to_conversion: Math.max(
        0,
        Math.floor((new Date(sale.created_at!).getTime() - dispatchedAt.getTime()) / (1000 * 60 * 60 * 24))
      ),
    }));

  if (rowsToInsert.length > 0) {
    await supabase.from("campaign_attribution" as never).insert(rowsToInsert as never);
  }
}

export function useCampaignDetail(campaignId: string | undefined) {
  return useQuery({
    queryKey: ["campaign-detail", campaignId],
    queryFn: async () => {
      if (!campaignId) return null;

      await backfillCampaignAttribution(campaignId);

      const [recipientsRes, attributionRes] = await Promise.all([
        supabase
          .from("campaign_recipients" as never)
          .select("*")
          .eq("campaign_id", campaignId)
          .order("created_at", { ascending: true }),

        supabase
          .from("campaign_attribution" as never)
          .select("*")
          .eq("campaign_id", campaignId),
      ]);

      if (recipientsRes.error) throw recipientsRes.error;
      if (attributionRes.error) throw attributionRes.error;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rawRecipients = (recipientsRes.data ?? []) as any[];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rawAttributions = (attributionRes.data ?? []) as any[];

      // Fetch owner names in bulk
      const ownerIds = [...new Set(rawRecipients.map((r) => r.owner_id as string))];
      const ownerNameMap: Record<string, string> = {};

      if (ownerIds.length > 0) {
        const { data: owners } = await supabase
          .from("owners")
          .select("id, name")
          .in("id", ownerIds)
          .is("deleted_at", null);
        (owners ?? []).forEach((o) => {
          ownerNameMap[o.id] = o.name ?? "";
        });
      }

      const recipients = rawRecipients.map((r) => mapRecipient(r, ownerNameMap));
      const attributions = rawAttributions.map(mapAttribution);
      const metrics = computeMetrics(recipients, attributions);

      return { recipients, attributions, metrics };
    },
    enabled: !!campaignId,
    staleTime: 30_000,
    refetchInterval: (query) => {
      // Keep polling while campaign is dispatching
      const data = query.state.data;
      if (!data) return false;
      const hasActive = data.recipients.some((r) => r.status === "pending");
      return hasActive ? 10_000 : false;
    },
  });
}
